import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Button, CustomSelect, DateTimePicker, Switch } from '@/components';
import { useEnterSubmit } from '@/common/hooks';
import { parseApiError } from '@/common/utils';
import { getCompanyEmployees, getGoogleWorkspaceStatus } from '../../api';
import { ContactPicker } from './ContactPicker';
import { CreateContactModal } from './CreateContactModal';
import {
  getBrowserTimeZone,
  DEFAULT_DURATION_MINUTES,
  DEFAULT_START_MINUTES,
  STATUS_META,
  APPOINTMENT_STATUSES,
  buildCreatePayload,
  buildUpdatePayload,
  buildTimeOptions,
  getAppointmentScheduleFields,
  minutesToTime,
  readAnyContactName,
  validateAppointmentForm,
  toDayKey,
  timeToMinutes,
  clampSlotToFuture,
  GOOGLE_CALENDAR_SCOPE,
} from '../utils';
import { useCreateAppointment, useUpdateAppointment } from '../hooks';
import {
  MeetSaveNotice,
  MeetDetail,
  MeetSyncWarning,
  readMeetLink,
  readMeetError,
  RECONNECT_GOOGLE_MESSAGE,
} from './MeetLinkBlock';

function toDateObj(dayKey) {
  if (!dayKey) return null;
  const [y, m, d] = dayKey.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function fromDateObj(date) {
  if (!date) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function FieldLabel({ children, required, htmlFor }) {
  return (
    <label
      htmlFor={htmlFor}
      className="text-[14px] font-medium text-text-secondary"
    >
      {children}
      {required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
  );
}

export function AppointmentFormModal({
  isOpen,
  onClose,
  companyId,
  appointment = null,
  initialSlot = null,
  canUpdateStatus = true,
  onBusyChange,
  initialContact = null,
  onCreated,
}) {
  const isEdit = !!appointment;
  const createMutation = useCreateAppointment(companyId);
  const updateMutation = useUpdateAppointment(companyId);
  const isPending = createMutation.isPending || updateMutation.isPending;
  const queryClient = useQueryClient();

  const [createContactOpen, setCreateContactOpen] = useState(false);
  const [savedAppointment, setSavedAppointment] = useState(null);
  const [createContactInitialName, setCreateContactInitialName] = useState('');

  const [values, setValues] = useState({
    contact_id: '',
    contact_label: '',
    assigned_employee_id: '',
    date: '',
    start_time: '',
    end_time: '',
    timezone: getBrowserTimeZone(),
    status: 'scheduled',
    also_assign_lead: false,
    create_meet_link: false,
  });

  const timeOptions = useMemo(() => {
    const baseOptions = buildTimeOptions(15);
    const today = toDayKey(new Date());
    const tz = getBrowserTimeZone();

    if (values.date === today) {
      const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: tz,
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }).formatToParts(new Date());
      const h = Number(parts.find((p) => p.type === 'hour')?.value || 0);
      const m = Number(parts.find((p) => p.type === 'minute')?.value || 0);
      const nowMinutes = h * 60 + m;

      return baseOptions.map((opt) => ({
        ...opt,
        disabled: timeToMinutes(opt.value) < nowMinutes,
      }));
    }
    return baseOptions;
  }, [values.date]);

  const endTimeOptions = useMemo(() => {
    const startMins = timeToMinutes(values.start_time);
    if (startMins == null) return timeOptions;
    return timeOptions.map((opt) => ({
      ...opt,
      disabled:
        !!opt.disabled || (timeToMinutes(opt.value) ?? Infinity) <= startMins,
    }));
  }, [timeOptions, values.start_time]);

  const [errors, setErrors] = useState({});

  useEffect(() => {
    onBusyChange?.(isPending);
    return () => onBusyChange?.(false);
  }, [isPending, onBusyChange]);

  useEffect(() => {
    if (!isOpen) return;
    const tz = getBrowserTimeZone();
    if (appointment) {
      const schedule = getAppointmentScheduleFields(appointment);
      setValues({
        contact_id: appointment.contact_id || appointment.contact?.id || '',
        contact_label:
          readAnyContactName(appointment.contact) ||
          appointment.contact_name ||
          [appointment.contact_first_name, appointment.contact_last_name]
            .filter(Boolean)
            .join(' ') ||
          '',
        assigned_employee_id:
          appointment.assigned_employee_id ||
          appointment.assigned_employee?.id ||
          '',
        date: schedule.date,
        start_time: schedule.start_time,
        end_time: schedule.end_time,
        timezone: schedule.timezone || tz,
        status: appointment.status || 'scheduled',
        also_assign_lead: !!appointment.also_assign_lead,
        create_meet_link: false,
      });
    } else {
      const dayKey = initialSlot?.dayKey || '';
      const start =
        initialSlot?.startMinutes != null
          ? initialSlot.startMinutes
          : DEFAULT_START_MINUTES;
      const end = start + DEFAULT_DURATION_MINUTES;
      setValues({
        contact_id: initialContact?.id || '',
        contact_label: initialContact?.label || '',
        assigned_employee_id: initialContact?.assignedEmployeeId || '',
        date: dayKey,
        start_time: minutesToTime(start),
        end_time: minutesToTime(Math.min(end, 24 * 60 - 1)),
        timezone: tz,
        status: 'scheduled',
        also_assign_lead: false,
        create_meet_link: false,
      });
    }
    setErrors({});
    setSavedAppointment(null);
  }, [
    isOpen,
    appointment,
    initialSlot,
    initialContact?.id,
    initialContact?.label,
    initialContact?.assignedEmployeeId,
  ]);

  const { data: googleStatus } = useQuery({
    queryKey: ['google-workspace-status', companyId],
    queryFn: () => getGoogleWorkspaceStatus(companyId),
    enabled: !!companyId && isOpen,
  });

  const googleConnected =
    googleStatus?.is_connected === true || googleStatus?.status === 'connected';
  const hasCalendarScope = (googleStatus?.scopes || []).some(
    (scope) => scope === GOOGLE_CALENDAR_SCOPE,
  );
  const existingMeetLink = readMeetLink(appointment);
  const existingMeetError = readMeetError(appointment);
  const showMeetOptIn = !existingMeetLink && googleConnected;

  const { data: employeesData, isLoading: employeesLoading } = useQuery({
    queryKey: ['appointment-employees', companyId],
    queryFn: () => getCompanyEmployees(companyId, { page_size: 200 }),
    enabled: !!companyId && isOpen,
  });

  const employees =
    employeesData?.results ||
    (Array.isArray(employeesData) ? employeesData : []);

  const employeeOptions = employees.map((emp) => ({
    value: String(emp.id),
    label:
      emp.full_name ||
      emp.name ||
      [emp.first_name, emp.last_name].filter(Boolean).join(' ') ||
      emp.email ||
      `Employee #${emp.id}`,
  }));

  if (
    values.assigned_employee_id &&
    !employeeOptions.some(
      (opt) => opt.value === String(values.assigned_employee_id),
    )
  ) {
    employeeOptions.push({
      value: String(values.assigned_employee_id),
      label:
        appointment?.assigned_employee_name ||
        `Employee #${values.assigned_employee_id}`,
    });
  }

  const setField = (key, value) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const submit = async () => {
    const nextErrors = validateAppointmentForm(values);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    try {
      if (isEdit) {
        const payload = buildUpdatePayload(values, appointment);
        if (Object.keys(payload).length === 0) {
          onClose?.();
          return;
        }
        await updateMutation.mutateAsync({
          appointmentId: appointment.id,
          payload,
        });
        toast.success('Appointment updated');
      } else {
        await createMutation.mutateAsync(buildCreatePayload(values));
        toast.success('Appointment created');
        onCreated?.();
      }
      onClose?.();
    } catch (err) {
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to save appointment');
    }
  };

  const formRef = useEnterSubmit({
    onSubmit: submit,
    isPending,
  });

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    if (!isPending) submit();
  };

  return (
    <>
      <form
        ref={formRef}
        onSubmit={handleSubmit}
        className="flex flex-col h-full overflow-hidden"
        autoComplete="off"
      >
        <div className="flex-1 overflow-y-auto custom-scrollbar p-4 flex flex-col gap-4 bg-white min-w-0">
          <div className="flex flex-col gap-2.5 min-w-0">
            <h4 className="text-[16px] leading-[24px] font-medium text-[#50AB3D] font-sf-pro-display">
              Contact & assignment
            </h4>
            <div className="bg-[#F5F5F5] rounded-[12px] p-3.5 flex flex-col gap-4 min-w-0">
              <div className="flex flex-col gap-2">
                <FieldLabel required>Contact</FieldLabel>
                <ContactPicker
                  companyId={companyId}
                  value={values.contact_id}
                  selectedLabel={values.contact_label}
                  disabled={isPending}
                  error={errors.contact_id}
                  onChange={({ id, name }) => {
                    setField('contact_id', id);
                    setField('contact_label', name);
                  }}
                  onCreateContact={(searchQuery) => {
                    setCreateContactInitialName(searchQuery || '');
                    setCreateContactOpen(true);
                  }}
                />
              </div>

              <div className="flex flex-col gap-2">
                <FieldLabel required>Assigned employee</FieldLabel>
                <CustomSelect
                  value={
                    values.assigned_employee_id
                      ? String(values.assigned_employee_id)
                      : ''
                  }
                  onChange={(v) => setField('assigned_employee_id', v)}
                  options={employeeOptions}
                  searchable
                  placeholder={
                    employeesLoading ? 'Loading...' : 'Select employee'
                  }
                  disabled={isPending || employeesLoading}
                  error={!!errors.assigned_employee_id}
                  className="h-11"
                />
                {errors.assigned_employee_id && (
                  <p className="text-red-500 text-xs font-medium">
                    {errors.assigned_employee_id}
                  </p>
                )}
              </div>

              {!appointment?.also_assign_lead && (
                <div className="flex items-start gap-3 min-w-0">
                  <Switch
                    checked={values.also_assign_lead}
                    onChange={(v) => setField('also_assign_lead', v)}
                    disabled={isPending}
                  />
                  <span className="text-sm font-medium text-gray-700 leading-5 min-w-0">
                    Also assign lead to this employee
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-2.5 min-w-0">
            <h4 className="text-[16px] leading-[24px] font-medium text-[#50AB3D] font-sf-pro-display">
              Schedule
            </h4>
            <div className="bg-[#F5F5F5] rounded-[12px] p-3.5 flex flex-col gap-4 min-w-0">
              <div className="flex flex-col gap-2">
                <FieldLabel required>Date</FieldLabel>
                <DateTimePicker
                  selected={toDateObj(values.date)}
                  onChange={(date) => {
                    const newDateKey = fromDateObj(date);
                    if (newDateKey === values.date) return;

                    setField('date', newDateKey);

                    if (newDateKey !== toDayKey(new Date())) {
                      setField('start_time', '00:00');
                      setField(
                        'end_time',
                        minutesToTime(DEFAULT_DURATION_MINUTES),
                      );
                    } else {
                      const clamped = clampSlotToFuture(newDateKey, null);
                      if (clamped) {
                        setField(
                          'start_time',
                          minutesToTime(clamped.startMinutes),
                        );
                        setField(
                          'end_time',
                          minutesToTime(
                            clamped.startMinutes + DEFAULT_DURATION_MINUTES,
                          ),
                        );
                      }
                    }
                  }}
                  mode="date"
                  allowPastDates={false}
                  disabled={isPending}
                  placeholderText="Select date"
                  className={`w-full h-11 px-4 rounded-xl bg-white border text-sm font-medium ${
                    errors.date ? 'border-status-error' : 'border-[#EBF4EA]'
                  }`}
                />
                {errors.date && (
                  <p className="text-red-500 text-xs font-medium">
                    {errors.date}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3 min-w-0">
                <div className="flex flex-col gap-2 min-w-0">
                  <FieldLabel required>Start time</FieldLabel>
                  <CustomSelect
                    value={values.start_time}
                    onChange={(v) => {
                      const startMins = timeToMinutes(v);
                      const endMins = timeToMinutes(values.end_time);
                      const nextEnd =
                        startMins != null &&
                        endMins != null &&
                        endMins <= startMins
                          ? minutesToTime(
                              Math.min(
                                startMins + DEFAULT_DURATION_MINUTES,
                                24 * 60 - 1,
                              ),
                            )
                          : values.end_time;

                      setValues((prev) => ({
                        ...prev,
                        start_time: v,
                        end_time: nextEnd,
                      }));
                      setErrors((prev) => {
                        const next = { ...prev };
                        delete next.start_time;
                        delete next.end_time;
                        return next;
                      });
                    }}
                    options={timeOptions}
                    disabled={isPending}
                    error={!!errors.start_time}
                    className="h-11"
                  />
                  {errors.start_time && (
                    <p className="text-red-500 text-xs font-medium">
                      {errors.start_time}
                    </p>
                  )}
                </div>
                <div className="flex flex-col gap-2 min-w-0">
                  <FieldLabel required>End time</FieldLabel>
                  <CustomSelect
                    value={values.end_time}
                    onChange={(v) => setField('end_time', v)}
                    options={endTimeOptions}
                    disabled={isPending}
                    error={!!errors.end_time}
                    className="h-11"
                  />
                  {errors.end_time && (
                    <p className="text-red-500 text-xs font-medium">
                      {errors.end_time}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {existingMeetLink ? (
            <div className="flex flex-col gap-2.5 min-w-0">
              <h4 className="text-[16px] leading-[24px] font-medium text-[#50AB3D] font-sf-pro-display">
                Google Meet
              </h4>
              <MeetDetail
                appointment={appointment}
                status={values.status}
                flush
              />
            </div>
          ) : existingMeetError || showMeetOptIn ? (
            <div className="flex flex-col gap-2.5 min-w-0">
              {existingMeetError ? (
                <div className="bg-[#FFF4E5] rounded-[12px] p-3.5">
                  <MeetSyncWarning message={existingMeetError} />
                </div>
              ) : null}
              {showMeetOptIn ? (
                <div className="flex flex-col gap-2 min-w-0">
                  {!hasCalendarScope ? (
                    <div className="bg-[#F5F5F5] rounded-[12px] p-3.5 flex flex-col gap-2 min-w-0">
                      <div className="flex items-start gap-3 min-w-0">
                        <Switch checked={false} disabled={true} />
                        <span className="text-sm font-medium text-gray-700 leading-5 min-w-0">
                          Google Meet
                        </span>
                      </div>
                      <p className="text-xs font-medium text-[#868686]">
                        {RECONNECT_GOOGLE_MESSAGE}
                      </p>
                    </div>
                  ) : (
                    <button
                      type="button"
                      disabled={isPending}
                      onClick={() =>
                        setField('create_meet_link', !values.create_meet_link)
                      }
                      className={`group flex items-center justify-between w-full p-3.5 rounded-[12px] border transition-all text-left outline-none focus-visible:ring-2 focus-visible:ring-[#50AB3D] ${
                        values.create_meet_link
                          ? 'bg-[#EBF4EA] border-[#50AB3D]/40 shadow-sm'
                          : 'bg-white border-[#EBEBEB] hover:border-[#50AB3D]/30 hover:bg-[#F9FAFB]'
                      }`}
                    >
                      <div className="flex items-center gap-3.5 min-w-0">
                        <div
                          className={`flex items-center justify-center w-10 h-10 rounded-full shrink-0 transition-colors ${
                            values.create_meet_link
                              ? 'bg-white shadow-sm'
                              : 'bg-[#F5F5F5] group-hover:bg-[#EBF4EA]'
                          }`}
                        >
                          <svg
                            className={`w-[22px] h-[22px] transition-all ${
                              values.create_meet_link
                                ? ''
                                : 'grayscale opacity-60 group-hover:grayscale-0 group-hover:opacity-100'
                            }`}
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <defs>
                              <linearGradient
                                id="bodyGrad"
                                x1="0%"
                                y1="0%"
                                x2="100%"
                                y2="100%"
                              >
                                <stop offset="0%" stopColor="#FFD33D" />
                                <stop offset="100%" stopColor="#FF9900" />
                              </linearGradient>
                              <linearGradient
                                id="lensGrad"
                                x1="0%"
                                y1="0%"
                                x2="100%"
                                y2="100%"
                              >
                                <stop offset="0%" stopColor="#FFAB2B" />
                                <stop offset="100%" stopColor="#F57C00" />
                              </linearGradient>
                              <radialGradient
                                id="pinkGlow"
                                cx="100%"
                                cy="50%"
                                r="60%"
                              >
                                <stop
                                  offset="0%"
                                  stopColor="#FF88AA"
                                  stopOpacity="0.5"
                                />
                                <stop
                                  offset="100%"
                                  stopColor="#FF88AA"
                                  stopOpacity="0"
                                />
                              </radialGradient>
                            </defs>

                            <path
                              d="M14 9.5L20 6C21.1 5.4 22.5 6.2 22.5 7.5V16.5C22.5 17.8 21.1 18.6 20 18L14 14.5V9.5Z"
                              fill="url(#lensGrad)"
                            />
                            <path
                              d="M14 9.5L20 6C21.1 5.4 22.5 6.2 22.5 7.5V16.5C22.5 17.8 21.1 18.6 20 18L14 14.5V9.5Z"
                              fill="url(#pinkGlow)"
                            />

                            <rect
                              x="2.5"
                              y="5.5"
                              width="13"
                              height="13"
                              rx="4"
                              fill="url(#bodyGrad)"
                            />
                            <rect
                              x="2.5"
                              y="5.5"
                              width="13"
                              height="13"
                              rx="4"
                              fill="url(#pinkGlow)"
                            />

                            <circle cx="6.5" cy="15" r="2" fill="white" />
                          </svg>
                        </div>
                        <div className="flex flex-col min-w-0">
                          <span className="text-[14px] font-semibold text-[#1F2241]">
                            Google Meet
                          </span>
                          <span className="text-[12px] text-[#868686] truncate mt-0.5">
                            Auto-generate meeting link
                          </span>
                        </div>
                      </div>
                      <div className="shrink-0 ml-3 pointer-events-none">
                        <Switch
                          checked={values.create_meet_link}
                          onChange={() => {}}
                        />
                      </div>
                    </button>
                  )}
                </div>
              ) : null}
            </div>
          ) : null}

          {isEdit && canUpdateStatus && (
            <div className="flex flex-col gap-2.5 min-w-0">
              <h4 className="text-[16px] leading-[24px] font-medium text-[#50AB3D] font-sf-pro-display">
                Status
              </h4>
              <div className="bg-[#F5F5F5] rounded-[12px] p-3.5 min-w-0">
                <div className="flex flex-col gap-2">
                  <FieldLabel>Status</FieldLabel>
                  <CustomSelect
                    value={values.status}
                    onChange={(v) => setField('status', v)}
                    options={APPOINTMENT_STATUSES.map((s) => ({
                      value: s,
                      label: STATUS_META[s].label,
                    }))}
                    disabled={isPending}
                    className="h-11"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {savedAppointment ? (
          <MeetSaveNotice appointment={savedAppointment} />
        ) : null}

        <div className="flex items-center px-4 py-4 border-t border-[#EBEBEB] shrink-0 bg-white">
          <Button
            type={savedAppointment ? 'button' : 'submit'}
            variant="primary"
            disabled={isPending}
            onClick={savedAppointment ? () => onClose?.() : undefined}
            className="h-[44px] w-full px-4 text-[15px] font-medium rounded-[8px] gap-2"
          >
            {savedAppointment ? (
              'Done'
            ) : isPending ? (
              'Saving...'
            ) : isEdit ? (
              'Save Changes'
            ) : (
              <>
                Schedule Appointment
                <span className="inline-flex items-center justify-center w-4 h-4 rounded-[4px] border border-white text-[12px] leading-none">
                  +
                </span>
              </>
            )}
          </Button>
        </div>
      </form>

      <CreateContactModal
        isOpen={createContactOpen}
        onClose={() => setCreateContactOpen(false)}
        companyId={companyId}
        initialName={createContactInitialName}
        onCreated={({ id, name }) => {
          // Auto-select the newly created contact
          setField('contact_id', id);
          setField('contact_label', name);
          // Close the mini-modal
          setCreateContactOpen(false);
          // Invalidate contact list so the picker re-fetches fresh data
          queryClient.invalidateQueries({
            queryKey: ['appointment-contacts', companyId],
          });
        }}
      />
    </>
  );
}
